import React, { type FC, useEffect, useMemo, useRef, useState } from "react";
import { CgSpinner } from "react-icons/cg";
import { useI18next } from "gatsby-plugin-react-i18next";

import CheckCircleIcon from "@components/icons/check_circle_icon";
import Modal from "@components/modal";
import CookieType, { CookieOptions } from "@constants/CookieType";
import Languages from "@data/languages.json";
import { composeRefs, useListKeyboardNav } from "@hooks/useListKeyboardNav";
import type Language from "@interfaces/Language";
import { useAuthenticationState } from "@stores/authentication";
import axios from "@utilities/axios";
import { loadCurrencies } from "@utilities/currencies";
import { cn, primaryRipple } from "@utilities/helper";
import Cookies from "js-cookie";
import { AnimatePresence, motion } from "motion/react";

const LangModal: FC<{
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
}> = ({ isModalOpen, setIsModalOpen }) => {
  const { t, changeLanguage, language } = useI18next();
  const authenticationState = useAuthenticationState();

  const [startLoadingLang, setStartLoadingLang] = useState<string>("");
  const updateLang = async (lang: string): Promise<void> => {
    setStartLoadingLang(lang);
    Cookies.set(CookieType.LANG, lang, CookieOptions);
    setIsModalOpen(false);
    void loadCurrencies(lang);
    if (authenticationState.isAuthenticated()) {
      const formData = new FormData();
      formData.append("action", "update-language");
      formData.append("language", lang);
      const changeLanguageUrl = "/my_account_details/";
      await axios.post(changeLanguageUrl, formData);
    }
    await changeLanguage(lang);
  };
  useEffect(() => {
    setStartLoadingLang("");
  }, [isModalOpen]);

  const enabledLanguages = useMemo(
    () => Languages.filter((lang: Language) => lang.enabled),
    [],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => {
        containerRef.current?.focus();
      }, 100);
    }
  }, [isModalOpen]);

  const { highlightedIndex, handleKeyDown, registerItem } =
    useListKeyboardNav<Language>({
      items: enabledLanguages,
      isActive: isModalOpen,
      onSelect: lang => {
        void updateLang(lang.id);
      },
      onEscape: () => {
        setIsModalOpen(false);
      },
    });

  return (
    <Modal
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      isCloseButtonExist={true}
      title={t("language") ?? ""}
      isContentScrollable={true}
    >
      <div
        ref={containerRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="flex flex-col gap-3 outline-none"
      >
        {enabledLanguages.map((lang: Language, i: number) => {
          const isSelected = lang.id === language;
          const isHighlighted = i === highlightedIndex;
          return (
            <button
              ref={composeRefs<HTMLButtonElement>(
                primaryRipple,
                registerItem(i),
              )}
              role="option"
              aria-selected={isSelected}
              key={lang.name}
              onClick={() => {
                void updateLang(lang.id);
              }}
              className={cn(
                "space-between focus:custom-primary-focus-ring group flex w-full items-center rounded-3xl !border-2 border-solid border-primary-200 p-3 shadow-lg shadow-gray-300 transition duration-300",
                {
                  "bg-primary": isSelected,
                  "bg-white hover:opacity-80": !isSelected,
                  "ring-2 ring-primary/40 ring-offset-1":
                    isHighlighted && !isSelected,
                },
              )}
            >
              <span
                className={cn(
                  "block w-full no-underline transition duration-300",
                  {
                    "text-white": lang.id === language,
                    "text-black group-hover:text-primary": lang.id !== language,
                  },
                )}
              >
                {lang.name}
              </span>
              <AnimatePresence>
                <>
                  {!(
                    startLoadingLang !== "" && lang.id === startLoadingLang
                  ) && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CheckCircleIcon className="size-5 text-white" />
                    </motion.span>
                  )}
                  {startLoadingLang !== "" && lang.id === startLoadingLang && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CgSpinner className="size-5 animate-spin text-primary" />
                    </motion.span>
                  )}
                </>
              </AnimatePresence>
            </button>
          );
        })}
      </div>
    </Modal>
  );
};

export default LangModal;
